import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { CursorPagination } from '../components/CursorPagination';
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge } from '../components/ui';
import { useCursorPagination } from '../hooks/useCursorPagination';
import { copy, formatUserSubtitle, formatYesNo } from '../lib/copy';
import { adminApi } from '../lib/api';
import { useProductScope } from '../context/ProductScopeContext';
import { downloadCsv } from '../lib/csv';
import { formatDate, formatNumber } from '../lib/utils';

function exportUsersCsv(items: Awaited<ReturnType<typeof adminApi.users>>['items']) {
  const header = ['id', 'first_name', 'username', 'plan', 'credits', 'agents', 'onboarded', 'created_at'];
  const rows = items.map((u) => [
    u.id,
    u.first_name,
    u.username ?? '',
    u.plan ?? '',
    String(u.credits_balance ?? ''),
    String(u.agents_count ?? ''),
    u.onboarded ? 'yes' : 'no',
    u.created_at,
  ]);
  downloadCsv('users.csv', header, rows);
}

export function UsersPage() {
  const scope = useProductScope();
  const [search, setSearch] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [plan, setPlan] = useState('');
  const [appPlan, setAppPlan] = useState('');
  const [studioPlan, setStudioPlan] = useState('');
  const pagination = useCursorPagination();
  const { reset } = pagination;

  const plansQuery = useQuery({
    queryKey: ['admin', 'plans', scope],
    queryFn: async () => {
      if (scope === 'studio') return adminApi.studioPlans();
      if (scope === 'app') return adminApi.appPlans();
      const [app, studio] = await Promise.all([adminApi.appPlans(), adminApi.studioPlans()]);
      return { items: [...app.items, ...studio.items] };
    },
  });

  useEffect(() => {
    reset();
  }, [submitted, plan, appPlan, studioPlan, reset]);

  const usersQueryFn = () => {
    if (scope === 'app') {
      return adminApi.appUsers({
        q: submitted || undefined,
        plan: plan || undefined,
        cursor: pagination.cursor,
        limit: pagination.pageSize,
      });
    }
    if (scope === 'studio') {
      return adminApi.studioUsers({
        q: submitted || undefined,
        plan: plan || undefined,
        cursor: pagination.cursor,
        limit: pagination.pageSize,
      });
    }
    return adminApi.platformUsers({
      q: submitted || undefined,
      appPlan: appPlan || undefined,
      studioPlan: studioPlan || undefined,
      cursor: pagination.cursor,
      limit: pagination.pageSize,
    });
  };

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['users', scope, submitted, plan, appPlan, studioPlan, pagination.page, pagination.cursor],
    queryFn: usersQueryFn,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(search.trim());
  };

  return (
    <div>
      <PageHeader
        title={copy.nav.users}
        description="Поиск по имени, почте, имени в Telegram или номеру Telegram."
        actions={
          data && data.items.length > 0 ? (
            <button type="button" className="ops-btn-secondary" onClick={() => exportUsersCsv(data.items)}>
              {copy.users.exportCsv}
            </button>
          ) : null
        }
      />
      <form onSubmit={handleSearch} className="mb-4 flex flex-wrap gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ops-muted" />
          <input
            type="search"
            placeholder="Имя, почта или Telegram..."
            className="ops-input pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="ops-input w-auto" value={plan} onChange={(e) => setPlan(e.target.value)}>
          <option value="">{copy.users.allPlans}</option>
          {(plansQuery.data?.items ?? []).map((p) => (
            <option key={p.product_plan_id} value={p.code}>
              {p.name}
            </option>
          ))}
        </select>
        {scope === 'platform' ? (
          <>
            <select
              className="ops-input w-auto"
              value={appPlan}
              onChange={(e) => setAppPlan(e.target.value)}
            >
              <option value="">APP тариф</option>
              {(plansQuery.data?.items ?? [])
                .filter((p) => p.product_code === 'APP')
                .map((p) => (
                  <option key={`app-${p.product_plan_id}`} value={p.code}>
                    {p.name}
                  </option>
                ))}
            </select>
            <select
              className="ops-input w-auto"
              value={studioPlan}
              onChange={(e) => setStudioPlan(e.target.value)}
            >
              <option value="">Studio тариф</option>
              {(plansQuery.data?.items ?? [])
                .filter((p) => p.product_code === 'STUDIO')
                .map((p) => (
                  <option key={`studio-${p.product_plan_id}`} value={p.code}>
                    {p.name}
                  </option>
                ))}
            </select>
          </>
        ) : null}
        <button type="submit" className="ops-btn-primary">
          {copy.common.search}
        </button>
      </form>

      {isLoading ? <LoadingState label="Загрузка пользователей..." /> : null}
      {isError ? <ErrorState message={copy.errors.loadUsers} onRetry={() => refetch()} /> : null}
      {data && data.items.length === 0 ? <EmptyState message="Пользователи не найдены." /> : null}

      {data && data.items.length > 0 ? (
        <div className="ops-card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Пользователь</th>
                  {scope === 'platform' ? (
                    <>
                      <th>APP</th>
                      <th>Studio</th>
                    </>
                  ) : (
                    <th>Тариф</th>
                  )}
                  {scope !== 'studio' ? <th>Кредиты</th> : null}
                  {scope !== 'studio' ? <th>Агенты</th> : null}
                  <th>Онбординг</th>
                  <th>Регистрация</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((user) => {
                  const subtitle = formatUserSubtitle(user.username);
                  return (
                    <tr key={user.id}>
                      <td>
                        <Link
                          to={`/${scope}/users/${user.id}`}
                          className="font-medium text-ops-text hover:underline"
                        >
                          {user.first_name}
                        </Link>
                        {subtitle ? <p className="text-xs text-ops-muted">{subtitle}</p> : null}
                      </td>
                      {scope === 'platform' ? (
                        <>
                          <td>
                            <StatusBadge status={user.app_plan ?? 'free'} kind="plan" />
                          </td>
                          <td>
                            <StatusBadge status={user.studio_plan ?? 'studio_free'} kind="plan" />
                          </td>
                        </>
                      ) : (
                        <td>
                          <StatusBadge
                            status={scope === 'studio' ? user.studio_plan ?? user.plan ?? 'free' : user.plan ?? 'free'}
                            kind="plan"
                          />
                        </td>
                      )}
                      {scope !== 'studio' ? <td className="text-ops-text">{formatNumber(user.credits_balance ?? 0)}</td> : null}
                      {scope !== 'studio' ? <td>{user.agents_count ?? 0}</td> : null}
                      <td>{formatYesNo(user.onboarded)}</td>
                      <td className="text-ops-muted">{formatDate(user.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <CursorPagination
            pageNumber={pagination.pageNumber}
            itemCount={data.items.length}
            canPrev={pagination.canPrev}
            hasNext={Boolean(data.next_cursor)}
            loading={isFetching}
            onPrev={pagination.goPrev}
            onNext={() => pagination.goNext(data.next_cursor)}
          />
        </div>
      ) : null}
    </div>
  );
}
